import React from 'react';
import {
  Chip,
  styled,
  emphasize,
} from '@mui/material';
import {
  Shield,
} from 'lucide-react';
import { AppInsight } from '@/types';

// ============================================================
// Styled breadcrumb chip (matches apphealth-agent)
// ============================================================
export const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const backgroundColor = isDark ? theme.palette.grey[50] : theme.palette.grey[100];
  return {
    backgroundColor,
    height: theme.spacing(3),
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover, &:focus': {
      backgroundColor: isDark ? theme.palette.grey[800] : emphasize(backgroundColor, 0.06),
    },
    '&:active': {
      boxShadow: theme.shadows[1],
      backgroundColor: isDark ? theme.palette.grey[800] : emphasize(backgroundColor, 0.12),
    },
  };
}) as typeof Chip;

// ============================================================
// EPA Validation Categories & Subcategories (static list)
// ============================================================
export const EPA_VALIDATION_CATEGORIES = [
  {
    category: 'Authentication & Authorization',
    subcategories: [
      'Spring Security configuration',
      'Authentication bypass vulnerabilities',
      'Session cookie security (HttpOnly, Secure, SameSite)',
      'Token validation and expiration',
      'Role-based access control (RBAC)',
      'Test/debug endpoints exposure',
    ],
  },
  {
    category: 'Input Validation',
    subcategories: [
      '@Valid annotations on request bodies',
      'Bean validation on DTOs',
      'File upload validation (size, type, content)',
      'Path traversal prevention',
      'SQL injection prevention',
    ],
  },
  {
    category: 'CORS & Security Headers',
    subcategories: [
      '@CrossOrigin configuration',
      'Content-Security-Policy header',
      'X-Frame-Options header',
      'X-Content-Type-Options header',
      'Strict-Transport-Security (HSTS)',
    ],
  },
  {
    category: 'Data Exposure & Logging',
    subcategories: [
      'Credentials not logged in plaintext',
      'No System.out.println in production',
      'Sensitive data not exposed in responses',
      'Auth tokens not logged',
    ],
  },
  {
    category: 'Error Handling',
    subcategories: [
      'Global error handler present',
      'Stack traces not exposed to clients',
      'Internal paths not exposed',
      'Database errors not exposed',
    ],
  },
  {
    category: 'Dependency & Configuration',
    subcategories: [
      'No hardcoded credentials',
      'Outdated dependencies with CVEs',
      'Swagger/OpenAPI secured in production',
      'Session timeout configured',
    ],
  },
  {
    category: 'API Design',
    subcategories: [
      'Proper HTTP verbs (no GET for mutations)',
      'Rate limiting implemented',
      'Pagination on list endpoints',
      'API versioning',
    ],
  },
];

// ============================================================
// SLA helper — derives SLA label from severity string
// ============================================================
export function getSla(severity: string): { label: string; color: string } {
  switch ((severity || '').toUpperCase()) {
    case 'BLOCKER':
    case 'CRITICAL': return { label: 'Fix within 24h', color: '#ef4444' };
    case 'HIGH':
    case 'MAJOR':    return { label: 'Fix within 7 days', color: '#f97316' };
    case 'MEDIUM':   return { label: 'Fix within 30 days', color: '#f59e0b' };
    default:         return { label: 'Fix within 90 days', color: '#3b82f6' };
  }
}

// ============================================================
// SonarQube / BlackDuck data types
// ============================================================
export interface SonarIssue {
  key: string;
  type: string;
  severity: string;
  message: string;
  component: string;
  line: number | null;
  rule: string;
  effort: string | null;
}

export interface SonarProject {
  repoName: string;
  branch: string;
  found: boolean;
  projectKey?: string;
  projectName?: string;
  metrics?: { bugs: number; vulnerabilities: number; codeSmells: number; securityHotspots: number; coverage: number | null; duplicatedLines: number | null };
  issuesBySeverity?: { blocker: number; critical: number; major: number };
  totalIssues?: number;
  recentAnalyses?: { date: string }[];
  issues?: SonarIssue[];
  message?: string;
}

export interface SonarData {
  organization: string;
  baseUrl: string;
  fetchedAt: string;
  storedAt?: string;
  projects: SonarProject[];
}

export interface BdComponent {
  componentName: string;
  componentVersion: string;
  vulnerabilityName?: string;
  cvss2?: number;
  cvss3?: number;
  severity?: string;
  description?: string;
  remediationStatus?: string;
  remediationComment?: string;
}

export interface BdProject {
  repoName: string;
  found: boolean;
  projectName?: string;
  versionName?: string;
  vulnerabilities?: { critical: number; high: number; medium: number; low: number; total: number };
  licenseRisk?: { high: number; medium: number; low: number };
  recentVersions?: { versionName: string; createdAt: string }[];
  vulnerableComponentsList?: BdComponent[];
  message?: string;
}

export interface BlackDuckData {
  baseUrl: string;
  fetchedAt: string;
  storedAt?: string;
  projects: BdProject[];
}

export interface GitleaksProject {
  repoName: string;
  found: boolean;
  secretsDetected?: number;
  branchProtected?: boolean;
}

export interface GitleaksData {
  fetchedAt: string;
  storedAt?: string;
  projects: GitleaksProject[];
}

export interface TrivyProject {
  repoName: string;
  found: boolean;
  imageScanPass?: boolean;
  imageCriticalCves?: number;
  imageHighCves?: number;
  imageAgeDays?: number | null;
  imageTag?: string | null;
  imageDigest?: string | null;
  imagePushedAt?: string | null;
  harborRepo?: string | null;
}

export interface TrivyData {
  fetchedAt: string;
  storedAt?: string;
  projects: TrivyProject[];
}

// ============================================================
// Section definitions for the TOC
// ============================================================
export interface SectionDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  hasContent: (d: AppInsight) => boolean;
}

// SonarQube/BlackDuck KPI panels were removed from the AI details scan page — that data
// now lives on the Dashboard's project-level view (fed from onboarding-configured
// credentials/branch, not re-fetched here). This page is AI security-score only.
export const SECTIONS: SectionDef[] = [
  {
    id: 'security-score',
    label: 'Security Score',
    icon: React.createElement(Shield, { size: 15 }),
    hasContent: (d) => !!(d.securityScore || d.aggregatedSecurityScore || d.repoManifest?.length || d.threatModelSummary?.findings?.length || d.threatModelSummary?.diagram || d.threatModelDiagram),
  },
];

// ============================================================
// Security Score constants
// ============================================================

// Category mapping for display names
export const SECURITY_CATEGORY_DISPLAY: Record<string, string> = {
  authentication: 'Authentication',
  authorization: 'Authorization',
  encryption: 'Encryption',
  inputValidation: 'Input Validation & Injection',
  logging: 'Logging & Monitoring',
  dependencyManagement: 'Dependency Management',
  networkSecurity: 'Network Security',
  secretsManagement: 'Secrets Management',
  aiSecurity: 'AI/LLM Security',
  apiSecurity: 'OWASP API Top 10',
  cloudSecurity: 'Container/Cloud Security',
  cicdSecurity: 'CI/CD Pipeline Security',
  businessLogic: 'Business Logic & Session',
  packageLibrarySecurity: 'Package/Library Security',
};

// Map LLM category strings to canonical breakdown keys (exact mirror of backend normalizeCategoryKey)
export const CATEGORY_KEY_MAP: Record<string, string> = {
  'authentication': 'authentication',
  'authorization': 'authorization',
  'encryption': 'encryption',
  'input validation': 'inputValidation',
  'logging': 'logging',
  'dependency management': 'dependencyManagement',
  'network security': 'networkSecurity',
  'secrets management': 'secretsManagement',
  'ai security': 'aiSecurity',
  'api security': 'apiSecurity',
  'cloud security': 'cloudSecurity',
  'ci/cd security': 'cicdSecurity',
  'cicd security': 'cicdSecurity',
  'business logic': 'businessLogic',
  'frontend security': 'inputValidation',
  'client-side security': 'inputValidation',
  'xss': 'inputValidation',
  'injection': 'inputValidation',
  'input sanitization': 'inputValidation',
  'database security': 'encryption',
  'data security': 'encryption',
  'data protection': 'encryption',
  'cryptography': 'encryption',
  'crypto': 'encryption',
  'container security': 'cloudSecurity',
  'kubernetes': 'cloudSecurity',
  'docker security': 'cloudSecurity',
  'iac security': 'cloudSecurity',
  'infrastructure': 'cloudSecurity',
  'file upload': 'inputValidation',
  'file upload security': 'inputValidation',
  'graphql': 'apiSecurity',
  'graphql security': 'apiSecurity',
  'api versioning': 'apiSecurity',
  'rest api': 'apiSecurity',
  'microservice security': 'networkSecurity',
  'microservices': 'networkSecurity',
  'race conditions': 'businessLogic',
  'session management': 'businessLogic',
  'error handling': 'logging',
  'monitoring': 'logging',
  'logging & monitoring': 'logging',
  'logging and monitoring': 'logging',
  'audit': 'logging',
  'audit trail': 'logging',
  'secrets': 'secretsManagement',
  'secret management': 'secretsManagement',
  'credential management': 'secretsManagement',
  'access control': 'authorization',
  'iam': 'authorization',
  'iam security': 'authorization',
  'iam least privilege': 'authorization',
  'rbac': 'authorization',
  'privilege escalation': 'authorization',
  'ai/llm security': 'aiSecurity',
  'llm security': 'aiSecurity',
  'machine learning': 'aiSecurity',
  'supply chain': 'dependencyManagement',
  'supply chain security': 'dependencyManagement',
  'dependency': 'dependencyManagement',
  'dependencies': 'dependencyManagement',
  'license compliance': 'dependencyManagement',
  'compliance': 'businessLogic',
  'data privacy': 'encryption',
  'privacy': 'encryption',
  'dos': 'networkSecurity',
  'denial of service': 'networkSecurity',
  'ddos': 'networkSecurity',
  'security headers': 'networkSecurity',
  'cors': 'networkSecurity',
  'tls': 'encryption',
  'ssl': 'encryption',
  'certificate': 'encryption',
  'pipeline security': 'cicdSecurity',
  'ci/cd': 'cicdSecurity',
  'build security': 'cicdSecurity',
  'deployment security': 'cicdSecurity',
  'security testing': 'cicdSecurity',
  'threat': 'businessLogic',
  'trust boundaries': 'networkSecurity',
  'attack surface': 'networkSecurity',
  // Package/Library Security — version outdation (direct + transitive)
  'package/library security': 'packageLibrarySecurity',
  'package library security': 'packageLibrarySecurity',
  'package security': 'packageLibrarySecurity',
  'library security': 'packageLibrarySecurity',
  'outdated packages': 'packageLibrarySecurity',
  'outdated dependencies': 'packageLibrarySecurity',
  'version management': 'packageLibrarySecurity',
  'version outdation': 'packageLibrarySecurity',
  'outdated versions': 'packageLibrarySecurity',
  'transitive dependencies': 'packageLibrarySecurity',
  'third-party libraries': 'packageLibrarySecurity',
  'third party libraries': 'packageLibrarySecurity',
};

const CANONICAL_CATEGORY_SET = new Set(Object.keys(SECURITY_CATEGORY_DISPLAY));

export function normalizeFindingCategory(category: string): string {
  const lower = (category || '').toLowerCase().trim();
  if (CATEGORY_KEY_MAP[lower]) return CATEGORY_KEY_MAP[lower];
  // If already a canonical key, return as-is
  if (CANONICAL_CATEGORY_SET.has(lower)) return lower;
  if (CANONICAL_CATEGORY_SET.has(category)) return category;
  return 'uncategorized';
}
